import type { PostResumo } from "@/lib/blog.functions";
// Portado de /tmp/astro/src/pages/tratamentos/estetica-integrada.astro (site Astro).
import { Hero } from "@/components/itv/tratamento/Hero";
import { Pilares } from "@/components/itv/tratamento/Pilares";
import { Dores } from "@/components/itv/tratamento/Dores";
import { CardsCapa } from "@/components/itv/tratamento/CardsCapa";
import { Medico } from "@/components/itv/tratamento/Medico";
import { Faq } from "@/components/itv/tratamento/Faq";
import { Conteudos } from "@/components/itv/tratamento/Conteudos";
import { CtaPratico } from "@/components/itv/tratamento/CtaPratico";
import mesojectGunAsset from "@/assets/estetica/mesoject-gun.webp.asset.json";
import unyqueProAsset from "@/assets/estetica/unyque-pro.webp.asset.json";

const pilares = [
  { icone: "/icones/search.svg", titulo: "Quem indica é o médico", texto: "A indicação vem de quem acompanha a sua composição corporal, não de um catálogo de procedimentos." },
  { icone: "/icones/shield.svg", titulo: "Três áreas de tratamento", texto: "Corporal, facial e capilar, com abordagem regenerativa: cada área com sua avaliação e seus protocolos." },
  { icone: "/icones/video-cam.svg", titulo: "Onde e como", texto: "Presencial em Bauru ou por teleconsulta para todo o Brasil. Atendimento particular." },
];

const queixas = [
  { icone: "/icones/flame.svg", titulo: "Flacidez após emagrecer", texto: "A pele que sobrou da perda de peso, no corpo e no rosto. Quanto antes o cuidado começa, melhor a resposta." },
  { icone: "/icones/moon.svg", titulo: "Gordura localizada", texto: "Pontos que resistem mesmo com o emagrecimento encaminhado." },
  { icone: "/icones/activity.svg", titulo: "Queda de cabelo", texto: "Comum durante o emagrecimento. Tem causa, tem tratamento e costuma responder bem." },
  { icone: "/icones/clipboard.svg", titulo: "Celulite e lipedema", texto: "Quadros diferentes que pedem avaliação: os protocolos do consultório incluem indicação para os dois." },
];

const areas = [
  { img: "/img/estetica/area-1.webp", badge: "ESTÉTICA CORPORAL", titulo: "Estética corporal", texto: "Flacidez, gordura localizada e contorno, com bioestimulador de colágeno, aplicação de enzimas e as tecnologias do consultório." },
  { img: "/img/estetica/area-2.webp", badge: "ESTÉTICA FACIAL", titulo: "Estética facial", texto: "Flacidez, sinais de envelhecimento e marcas de expressão, com foco em firmeza e qualidade da pele." },
  { img: "/img/estetica/area-3.webp", badge: "TRATAMENTO CAPILAR", titulo: "Tratamento capilar", texto: "Queda de cabelo em duas frentes: as causas internas, investigadas com exames, e o estímulo local com intradermoterapia sem agulhas." },
];

// Tecnologias — Figma 101:377.
const tecnologias = [
  { nome: "Unyque Pro", texto: "Plataforma que reúne radiofrequência, crioterapia e ultrassom no mesmo equipamento, o que permite combinar tecnologias em um único protocolo. Indicada para flacidez, gordura localizada, celulite e lipedema.", imagem: unyqueProAsset.url, largura: 380, altura: 504 },
  { nome: "Mesoject Gun", texto: "Sistema de intradermoterapia que aplica ativos na pele e no couro cabeludo sem agulhas, por eletroporação. Usado nos protocolos corporal, facial e capilar.", imagem: mesojectGunAsset.url, largura: 768, altura: 768 },
];

// Menção a "Hospital Albert Einstein" omitida: não consta na lista de credenciais autorizadas.
const paragrafosMedico = [
  "A indicação estética aqui vem do médico que acompanha a sua composição corporal, e procedimento sem indicação não é oferecido.",
  "O Dr. Thiago tem três especialidades registradas, incluindo Nutrologia, e conduz a estética como parte do plano clínico. Formação pela UNIFESP-EPM, em 2010.",
];

export const faq = [
  { pergunta: "A estética substitui o tratamento de emagrecimento?", resposta: "Não. A base é o cuidado clínico. A estética trata o que a mudança de composição corporal deixa, como flacidez e queda de cabelo." },
  { pergunta: "Quando começar a cuidar da flacidez?", resposta: "Durante o processo, não depois. A flacidez e a queda de cabelo do emagrecimento respondem melhor quando o cuidado começa junto com a perda de peso." },
  { pergunta: "Quantas sessões vou precisar?", resposta: "Depende do protocolo indicado e da resposta do seu caso. O número é definido na avaliação, não antes dela." },
  { pergunta: "Os procedimentos doem?", resposta: "Varia conforme o protocolo. A intradermoterapia do consultório é feita sem agulhas, por eletroporação." },
  { pergunta: "Funciona por teleconsulta?", resposta: "A avaliação e a indicação podem começar por vídeo. A aplicação dos protocolos é presencial, em Bauru." },
];


export function EsteticaRegenerativa({ artigos }: { artigos: PostResumo[] }) {
  return (
    <div className="pag-estetica-regenerativa">
      <Hero
        trilha={[{ nome: "Home", href: "/" }, { nome: "Tratamentos", href: "/tratamentos/" }, { nome: "Estética e medicina regenerativa" }]}
        sobretitulo="TRATAMENTO"
        linha1="Estética"
        linha2="integrada."
        chamada="Protocolos para flacidez, gordura localizada, qualidade da pele e queda de cabelo, indicados após avaliação médica individual, principalmente durante e após mudanças na composição corporal."
        imagem="/img/estetica/hero.webp"
      />

      <Pilares
        sobretitulo="ESTÉTICA INTEGRADA"
        titulo="Estética com indicação médica"
        chamada="A indicação vem do médico que acompanha a sua composição corporal e sabe em que fase da transformação você está. Os protocolos entram no mesmo plano do tratamento de base, no tempo certo: a flacidez e a queda de cabelo do emagrecimento respondem melhor quando o cuidado começa durante o processo."
        itens={pilares}
      />

      <Dores
        sobretitulo="AS QUEIXAS MAIS COMUNS"
        titulo="Flacidez, gordura localizada e queda de cabelo"
        chamada="Quatro queixas frequentes no consultório, principalmente de quem emagreceu:"
        itens={queixas}
      />

      <CardsCapa sobretitulo="ÁREAS DE TRATAMENTO" titulo="As três áreas" cards={areas} />

      <section className="tecnologias">
        <div className="container">
          <p className="sobretitulo">TECNOLOGIAS</p>
          <h2>Os equipamentos do consultório</h2>
          <ul className="tec-grade">
            {tecnologias.map((t) => (
              <li className="tec" key={t.nome}>
                <div className="tec-foto">
                  <img src={t.imagem} alt={`Aparelho ${t.nome}`} width={t.largura} height={t.altura} loading="lazy" />
                </div>
                <div className="tec-texto">
                  <h3>{t.nome}</h3>
                  <p>{t.texto}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Medico paragrafos={paragrafosMedico} />
      <Faq titulo="Dúvidas sobre os tratamentos estéticos" itens={faq} />
      <Conteudos titulo="Continue lendo sobre estética" artigos={artigos} />
      <CtaPratico linha1="Comece seu tratamento de" linha2="estética. Escolha o formato." />
    </div>
  );
}
